import '../Sass/App.scss';
import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import { Card } from './Card2';
import { DocNav } from './DocNav';
import { CodebaseHeader } from './CodebaseHeader';
import { Modify } from './Modify';
import { apiFetch } from '../Helpers/api';

let cardWidth = 300;

export function Docs(props) {

    const [deck, setDeck] = useState([])
    const [dataLoaded, setDataLoaded] = useState(false)
    const [cardInFocus, setCardInFocus] = useState([])
    const [modalOpen, setModalOpen] = useState(false)
    const [editing, setEditing] = useState(false)
    const [canEdit, setCanEdit] = useState(false)
    const [canPlaytest, setCanPlaytest] = useState(false)
    const [codebaseInfo, setCodebaseInfo] = useState()
    const [instructions, setInstructions] = useState([])
    const [loadError, setLoadError] = useState('')
    const [editCardOpen, setEditCardOpen] = useState(false)
    const [currentCard, setCurrentCard] = useState()
    const [cardSaveError, setCardSaveError] = useState('')
    const [codebase, setCodebase] = useState(props.codebaseId || 1)
    const dev_standards = ['function','listener','procedure','program','special']

    useEffect(() => {
        setLoadError('')
        apiFetch("docs.php?codebase_id=" + encodeURIComponent(codebase))
            .then((res) => res.json())
            .then((json) => {
                if (json.error) {
                    setLoadError(json.error)
                    setCanEdit(false)
                    setCanPlaytest(false)
                    setEditing(false)
                    setDataLoaded(true)
                    return
                }
                setCodebaseInfo(json.data.info)
                setInstructions(json.data.instructions)
                if (json.data.deck.length > 0 && json.data.deck[0].cards.length > 0) {
                    setCardInFocus(json.data.deck[0].cards[0])
                }
                setDeck(json.data.deck)
                setCodebaseInfo(json.data.info)
                setCanEdit(Boolean(json.data.permissions?.can_edit))
                setCanPlaytest(Boolean(json.data.permissions?.can_playtest))
                setEditing(Boolean(json.data.permissions?.can_edit))
                setDataLoaded(true)
            })
    }, [codebase])

    function selectCodebase(id) {
        setDataLoaded(false);
        setCodebase(id);
    }

    function expandCard(cardData) {
        setCardInFocus(cardData);
        setModalOpen(true);
    }

    function closeCard(cardData) {
        setModalOpen(false);
    }

    function newCardData(cardType = 'function') {
        return {
            codebase_id: codebase,
            card_type: cardType,
            quantity: 1,
            name_1: 'new ',
            name_2: 'card',
            argument: '',
            include_parentheses: 0,
            suffix: ';',
            description_1: 'Describe what this card does.',
            description_2: '',
            description: 'Describe what this card does.',
            header_1: '',
            header_2: '',
            then_text: '',
            else_text: '',
            x_level: 1,
            image: '',
            special: dev_standards.includes(cardType) ? '' : cardType,
            special_image: '',
            back_image: '',
            program_on_init: 0,
            deck: '',
            flippable: 0
        };
    }

    function openCardEditor(cardData, cardSetIndex = null, cardIndex = null) {
        setCardSaveError('');
        setCurrentCard({
            data: {...cardData},
            cardSetIndex: cardSetIndex,
            cardIndex: cardIndex
        });
        setEditCardOpen(true);
    }

    function addCard(cardType) {
        openCardEditor(newCardData(cardType));
    }

    function upsertCardInDeck(savedCard, previousCardType) {
        const nextDeck = deck.map((cardSet) => ({
            ...cardSet,
            cards: cardSet.cards.filter((card) => String(card.card_id) !== String(savedCard.card_id))
        })).filter((cardSet) => cardSet.cards.length > 0);

        const targetType = savedCard.card_type || previousCardType || 'function';
        const targetSetIndex = nextDeck.findIndex((cardSet) => cardSet.card_type === targetType);

        if (targetSetIndex >= 0) {
            nextDeck[targetSetIndex] = {
                ...nextDeck[targetSetIndex],
                cards: [...nextDeck[targetSetIndex].cards, savedCard]
            };
        } else {
            nextDeck.push({
                card_type: targetType,
                cards: [savedCard]
            });
        }

        nextDeck.sort((a, b) => a.card_type.localeCompare(b.card_type));
        setDeck(nextDeck);
    }

    async function saveCard(index, data) {
        setCardSaveError('');
        const previousCardType = currentCard?.data?.card_type;
        const payload = {
            ...data,
            codebase_id: data.codebase_id || codebase
        };

        try {
            const res = await apiFetch("editcard.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const json = await res.json();

            if (!res.ok || json.error) {
                throw new Error(json.error || 'Could not save card.');
            }

            upsertCardInDeck(json.data.card || payload, previousCardType);
            setEditCardOpen(false);
        } catch (error) {
            setCardSaveError(error.message || 'Could not save card.');
        }
    }

    return (
        <Container className="main-content" maxWidth={'xl'}>
            <>
                <Grid container spacing={2}> 
                    <Grid md={3} className='docs-nav' sx={{display: {xs: 'none', sm: 'none', md: 'block'}}}>
                        <DocNav
                            codebase={codebase}
                            codebaseInfo={codebaseInfo}
                            deck={deck}
                            selectCodebase={selectCodebase}
                            expandCard={expandCard}
                            changePage={props.changePage}
                            editing={editing}
                            setEditing={setEditing}
                            canEdit={canEdit}
                            canPlaytest={canPlaytest}
                        />
                    </Grid>
                    <Grid xs={12} sm={12} md={9}> 
                        { dataLoaded ? ( 
                            loadError ? (
                                <div className="auth-error">{loadError}</div>
                            ) :
                            <>
                            <CodebaseHeader codebaseId = {codebase} editing={editing && canEdit} canEdit={canEdit} gameData={codebaseInfo} instructions={instructions} />
                            { deck.map(cardSet => 
                                <>
                                <h2>{cardSet.card_type}{editing && canEdit && <button className='il-button' onClick={() => addCard(cardSet.card_type)}>add</button>}</h2>
                                <div className="docs-row" style={{ minHeight: cardWidth * 1.5 }}>
                                { cardSet.cards.map((card, cardIndex) => 
                                    <div className="docs-card-wrapper" key={card.card_id || card.id || cardIndex}>
                                        <Card cardData={card} specialImage={codebaseInfo.special_image || ""} cardWidth={cardWidth} showQuantity={true} notDraggable clickHandler={expandCard} />
                                        {editing && canEdit && <div className='edit-card-button'><button className='il-button' onClick={() => openCardEditor(card, null, cardIndex)}>edit</button></div>}
                                    </div>) }
                                </div>
                                </>
                            )}
                            {editing && canEdit && <button className='cli-button' onClick={() => addCard('function')}>add new card type</button>}
                            </>
                        ) : <LinearProgress color="success" /> 
                        }
                    </Grid>
                </Grid>
                { dataLoaded && <Dialog
                    open={modalOpen}
                    onClose={closeCard}
                    PaperProps={{sx: {bgcolor: 'transparent'}}}
                    >
                        <Card cardData={cardInFocus} specialImage={codebaseInfo.special_image} cardWidth={500} showQuantity={true} notDraggable clickHandler={() => {}}/>
                </Dialog> }
                { dataLoaded && currentCard && <Dialog
                    open={editCardOpen}
                    maxWidth='xl'
                    fullWidth={true}
                    onClose={() => setEditCardOpen(false)}
                    PaperProps={{sx: {bgcolor: 'black', border: 1, borderColor: 'white', padding: 3, color: 'white'}}}
                    >
                        <Modify
                            cardData={currentCard.data}
                            indexInSeries={currentCard.cardIndex}
                            changeHandler={saveCard}
                            setModifyOpen={setEditCardOpen}
                            heading={currentCard.data.card_id ? 'Edit codebase card' : 'Add codebase card'}
                            explanation={'Changes saved here update this card record in the codebase.'}
                            error={cardSaveError}
                        />
                </Dialog> }
            </>
        </Container>
    )
}
